export const wizePhoneObject = {
  intlNumber: "",
  countryCode: "",
  lineNumber: "",
};

export const emptyPhone = () => ({ ...wizePhoneObject });

const lengthOfLineNumber = {
  1: { min: 10, max: 10 },
  45: { min: 8, max: 8 },
  49: { min: 10, max: 11 },
  55: { min: 10, max: 11 },
  91: { min: 10, max: 10 },
  972: { min: 9, max: 9 },
};

const DEFAULT_LINE_LENGTH = { min: 10, max: 10 };

const PHONE_ERROR_LABELS = {
  too_short: "The phone number is too short",
  too_long: "The phone number is too long",
  us_invalid: "Please enter a valid US phone number",
  dk_invalid: "Please enter a valid Denmark phone number",
};

const getLengthRange = (countryCode) =>
  lengthOfLineNumber[countryCode] || DEFAULT_LINE_LENGTH;

const SORTED_DIAL_CODES = Object.keys(lengthOfLineNumber).sort(
  (a, b) => b.length - a.length
);

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const inferCountryCodeFromIntlDigits = (intlDigits) => {
  if (!intlDigits) return "";

  for (const code of SORTED_DIAL_CODES) {
    if (!intlDigits.startsWith(code)) continue;

    const candidateLine = intlDigits.slice(code.length);
    if (!candidateLine) continue;

    const { min, max } = getLengthRange(code);
    const length = effectiveLineLength(candidateLine);
    if (length >= min && length <= max) {
      return code;
    }
  }

  return "";
};

const buildIntlDigits = (countryCode, lineNumber, intlDigits = "") => {
  if (intlDigits) return intlDigits;
  if (countryCode && lineNumber) return `${countryCode}${lineNumber}`;
  return lineNumber || "";
};

export const isProfilePhoneEmpty = (phone) => {
  if (!phone) return true;
  if (typeof phone === "string") {
    const raw = digitsOnly(phone);
    return !raw || raw.length <= 4;
  }
  const line = phone.lineNumber || "";
  const intl = digitsOnly(phone.intlNumber);
  if (!line && (!intl || intl.length <= 4)) {
    return true;
  }
  return false;
};

export const withLineNumber = (phone) => {
  if (!phone || isProfilePhoneEmpty(phone)) return null;

  let countryCode = digitsOnly(phone.countryCode);
  let lineNumber = digitsOnly(phone.lineNumber);
  const intlDigits = digitsOnly(phone.intlNumber);

  if (!countryCode && intlDigits) {
    countryCode = inferCountryCodeFromIntlDigits(intlDigits);
  }

  if (!lineNumber && intlDigits) {
    lineNumber =
      countryCode && intlDigits.startsWith(countryCode)
        ? intlDigits.slice(countryCode.length)
        : intlDigits;
  }

  return {
    intlNumber: buildIntlDigits(countryCode, lineNumber, intlDigits),
    countryCode,
    lineNumber,
  };
};

const effectiveLineLength = (lineNumber) => {
  let length = lineNumber.length;
  if (lineNumber.startsWith("0")) {
    length -= 1;
  }
  return length;
};

export const validation = (phone) => {
  if (isProfilePhoneEmpty(phone)) return null;

  const enriched = withLineNumber(phone);
  if (!enriched) return null;

  const { countryCode, lineNumber } = enriched;
  if (!lineNumber.length) return null;

  const { min, max } = getLengthRange(countryCode);
  const length = effectiveLineLength(lineNumber);

  if (length < min) return "too_short";
  if (length > max) return "too_long";
  if (countryCode === "1" && /^[01]/.test(lineNumber)) return "us_invalid";
  if (countryCode === "45" && /^0/.test(lineNumber)) return "dk_invalid";

  return null;
};

export const getPhoneErrorMessage = (phone) => {
  const code = validation(phone);
  if (!code) return "";
  return PHONE_ERROR_LABELS[code] || "Please enter a valid phone number";
};

export const handlePhoneChange = (value, countryData = {}) => {
  const countryCode = digitsOnly(countryData.dialCode);
  const digits = digitsOnly(value);
  let lineNumber = digits;
  let intlNumber = digits;

  if (countryCode && digits.startsWith(countryCode)) {
    lineNumber = digits.slice(countryCode.length);
    intlNumber = digits;
  } else if (countryCode && digits) {
    lineNumber = digits;
    intlNumber = `${countryCode}${digits}`;
  }

  return {
    intlNumber,
    countryCode,
    lineNumber,
  };
};

export const phoneToInputValue = (phone) => {
  if (!phone) return "";
  if (typeof phone === "string") return digitsOnly(phone);

  const intlDigits = digitsOnly(phone.intlNumber);
  if (intlDigits) return intlDigits;

  const countryCode = digitsOnly(phone.countryCode);
  const lineNumber = digitsOnly(phone.lineNumber);
  if (countryCode && lineNumber) return `${countryCode}${lineNumber}`;
  if (lineNumber) return lineNumber;

  return "";
};

export const toPhoneInputValue = phoneToInputValue;

export const normalizePhoneForV2Api = (phone) => {
  const enriched = withLineNumber(phone);
  if (!enriched) return null;

  const { countryCode, lineNumber } = enriched;
  let intlDigits = enriched.intlNumber;

  if (!intlDigits && countryCode && lineNumber) {
    intlDigits = `${countryCode}${lineNumber}`;
  }

  return {
    intlNumber: intlDigits ? `+${intlDigits}` : "",
    countryCode,
    lineNumber,
  };
};

export const normalizeSubAccountPhone = (phone) => {
  if (!phone) return emptyPhone();
  if (typeof phone === "string") {
    const intlDigits = digitsOnly(phone);
    if (!intlDigits) return emptyPhone();

    const countryCode = inferCountryCodeFromIntlDigits(intlDigits);
    const lineNumber = countryCode ? intlDigits.slice(countryCode.length) : intlDigits;

    return {
      intlNumber: intlDigits,
      countryCode,
      lineNumber,
    };
  }

  let countryCode = digitsOnly(phone.countryCode);
  const intlDigits = digitsOnly(phone.intlNumber || phone.number);
  let lineNumber = digitsOnly(phone.lineNumber);

  if (!countryCode && intlDigits) {
    countryCode = inferCountryCodeFromIntlDigits(intlDigits);
  }

  if (!lineNumber && intlDigits) {
    lineNumber =
      countryCode && intlDigits.startsWith(countryCode)
        ? intlDigits.slice(countryCode.length)
        : intlDigits;
  }

  return {
    intlNumber: buildIntlDigits(countryCode, lineNumber, intlDigits),
    countryCode,
    lineNumber,
  };
};

const phoneService = {
  wizePhoneObject,
  emptyPhone,
  isProfilePhoneEmpty,
  validation,
  getPhoneErrorMessage,
  handlePhoneChange,
  phoneToInputValue,
  toPhoneInputValue,
  normalizePhoneForV2Api,
  normalizeSubAccountPhone,
  withLineNumber,
};

export default phoneService;
